import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError, api } from "./api";

export interface CurrentUser {
    id: string;
    tenantId?: string;
    email: string;
    status: string;
    roles: string[];
    createdAt: string;
}

const ME_KEY = ["auth", "me"] as const;

/**
 * useCurrentUser returns the authenticated user, or null if 401.
 * Other API errors are re-thrown so the UI can surface them.
 *
 * Cached forever (until login/logout invalidates) — no need to repoll on
 * every component mount; the cookie is the source of truth.
 */
export function useCurrentUser() {
    return useQuery({
        queryKey: ME_KEY,
        queryFn: async () => {
            try {
                return await api.get<CurrentUser>("/api/v2/me");
            } catch (err) {
                if (err instanceof ApiError && err.status === 401) {
                    return null;
                }
                throw err;
            }
        },
        staleTime: Infinity,
        retry: false,
    });
}

interface LoginInput {
    email: string;
    password: string;
}

/**
 * Backend may respond to /auth/login with EITHER the user (no MFA) or an
 * MFA challenge (factors enrolled). The discriminator is `mfaRequired`.
 */
export type LoginResponse =
    | { mfaRequired: false; user: CurrentUser }
    | { mfaRequired: true; challengeToken: string; availableMethods: string[]; expiresIn: number };

export function useLogin() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: LoginInput): Promise<LoginResponse> => {
            const raw = await api.post<
                | (CurrentUser & { mfaRequired?: false })
                | { mfaRequired: true; challengeToken: string; availableMethods: string[]; expiresIn: number }
            >("/auth/login", input);
            if ("mfaRequired" in raw && raw.mfaRequired) {
                return {
                    mfaRequired: true,
                    challengeToken: raw.challengeToken,
                    availableMethods: raw.availableMethods,
                    expiresIn: raw.expiresIn,
                };
            }
            const user = raw as CurrentUser;
            return { mfaRequired: false, user };
        },
        onSuccess: (res) => {
            if (!res.mfaRequired) qc.setQueryData(ME_KEY, res.user);
        },
    });
}

/** Re-export for the MFA submit hook to write the user into cache. */
export const ME_QUERY_KEY = ME_KEY;

interface RegisterInput {
    email: string;
    password: string;
}

export function useRegister() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: RegisterInput) => api.post<CurrentUser>("/auth/register", input),
        onSuccess: (user) => {
            qc.setQueryData(ME_KEY, user);
        },
    });
}

export function useLogout() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => api.post<void>("/auth/logout"),
        onSuccess: () => {
            qc.setQueryData(ME_KEY, null);
            qc.clear();
        },
    });
}

/** True when /auth/register can still be called (zero users in DB). */
export function isFirstUserError(err: unknown): boolean {
    return (
        err instanceof ApiError &&
        err.status === 403 &&
        typeof err.body === "object" &&
        err.body !== null &&
        "error" in err.body &&
        typeof err.body.error === "string" &&
        err.body.error.includes("self-registration is closed")
    );
}
